import React from 'react';
import ReactDOM from 'react-dom/client';
import axios from 'axios';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { actingTeamHeaderValue } from './constants/qaTeams';
import { loggedPage } from './utils/featureEvents';

// Attach the originating frontend route to every request to OUR backend so
// the BE can log which page each ACP/tool call came from (X-Clara-Page is
// whitelisted in clarab CORS allowedHeaders). Scoped strictly to
// REACT_APP_API_URL targets — third-party APIs must never receive the custom
// header or their CORS preflights would start failing.
const CLARA_API_BASE = process.env.REACT_APP_API_URL;

// The acting workspace (X-QA-Team) scopes QA MANAGER ONLY — every other
// feature keeps the user's own team. These are the QA Manager API surfaces:
//   /api/qa  /api/appeals  /api/rubrics  /api/intercom-export  /api/reports
// '/api/qa' also covers '/api/qa-logs' (and '/api/qa/*') by prefix — that is
// intentional, do NOT "fix" it into an exact match. Sending the header to
// everything is what emptied a switched admin's /api/tl page, and the same
// leak applies to /api/goals, /api/kyc-goals, /api/payments-goals,
// /api/scraping, /api/user-management, /api/developer and /api/scores, which
// now mount the teamScope middleware too.
const QA_TEAM_HEADER_PREFIXES = [
  '/api/qa',
  '/api/appeals',
  '/api/calibration',
  '/api/rubrics',
  '/api/intercom-export',
  '/api/reports',
];

// config.url may be absolute (API base + path) or relative. Anything we cannot
// resolve to a Clara API path counts as NOT a QA Manager surface (no header).
const isQaManagerUrl = (url) => {
  if (!url) return false;
  let path = url;
  if (CLARA_API_BASE && path.startsWith(CLARA_API_BASE)) {
    path = path.slice(CLARA_API_BASE.length);
  } else if (/^https?:\/\//i.test(path)) {
    return false;
  }
  if (!path.startsWith('/')) path = `/${path}`;
  // The ACP relay lives under /api/qa/acp/* but is not QA-Manager data: the /acp
  // workbench, the tools and the quick view read the ACP as the user's own team.
  // With a kick/hosts selection still in the tab, forwarding the acting team here
  // made the backend's "ACP is not available for the QA team you are acting as"
  // gate 403 every relay call — which the panel then showed as an expired session
  // (2026-09-18). Inside QA Manager the ACP panel is hidden for those teams anyway.
  if (path.startsWith('/api/qa/acp')) return false;
  return QA_TEAM_HEADER_PREFIXES.some((prefix) => path.startsWith(prefix));
};

axios.interceptors.request.use((config) => {
  try {
    const url = config.url || '';
    const isClaraApi = CLARA_API_BASE
      ? url.startsWith(CLARA_API_BASE) || (!/^https?:\/\//i.test(url) && !config.baseURL)
      : false;
    if (isClaraApi && typeof window !== 'undefined') {
      config.headers = config.headers || {};
      // Respect an explicitly set header — components that make calls NOT
      // attributable to the current page (e.g. the floating ACP Quick View)
      // tag themselves so usage metrics don't credit the underlying tool.
      if (!config.headers['X-Clara-Page']) {
        config.headers['X-Clara-Page'] = loggedPage(window.location.pathname);
      }
      // Multi-team QA split: forward the admin acting-team selection. Which
      // stored values may be forwarded is decided by the registry itself
      // (actingTeamHeaderValue in constants/qaTeams.js) so a 4th team can't
      // silently stop the header being sent from here — that import costs
      // nothing, this entry module already pulls in ./App. Absent header = act
      // as own team, so the default 'qa' is never sent (the backend 403s the
      // header for non-admin/developer roles).
      if (isQaManagerUrl(url)) {
        const actingTeam = actingTeamHeaderValue();
        if (actingTeam) config.headers['X-QA-Team'] = actingTeam;
      }
    }
  } catch (e) { /* tracking must never break a request */ }
  return config;
});

// Workaround for facebook/react#11538: Chrome's "Translate to English" wraps text
// nodes in <font> tags, which makes React's reconciler crash with
// "Failed to execute 'insertBefore'/'removeChild' on 'Node'" when navigating
// between tickets. Patching these DOM methods to silently no-op on stale
// references lets React recover instead of leaving a blank white page.
if (typeof Node === 'function' && Node.prototype) {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function (child) {
    if (child.parentNode !== this) return child;
    return originalRemoveChild.apply(this, arguments);
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function (newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) return newNode;
    return originalInsertBefore.apply(this, arguments);
  };
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// Logs Core Web Vitals (LCP, CLS, INP, FCP, TTFB) to console for measurement.
reportWebVitals();
